import React, { useEffect, useState } from 'react'
import { useSelector } from "react-redux"
import { selectUser } from 'src/stores/userSlice';
import { selectEmployees } from 'src/stores/employeeSlice';
import ms from "ms";
import moment from 'moment';
import ApiClient from "../ApiClient"
import { GridToolbar } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import { notify } from "../utils/ToastUtils";
import CentralizedTable from "../components/CentralizedTable";
import ActionConfirmModal from "../components/ActionConfirmModal";
import { ClipboardList, History, CheckCircle2, XCircle, Trash2, X } from "lucide-react";
import { TextField, Select, MenuItem, Autocomplete, Chip } from "@mui/material";
import AcceptAllWfhModal from "../components/AcceptAllWfhModal";
import UserAvatar from "../components/UserAvatar";

function WFHTabSA() {
    const user = useSelector(selectUser)
    const employees = useSelector(selectEmployees)
    
    const [rows, setRows] = useState([]);
    const [wfhRows, setWfhRows] = useState([]);
    
    const [minDate, setMinDate] = useState(null)
    const [maxDate, setMaxDate] = useState(null)
    const [loading, setLoading] = useState({ isInitial: true, pending: true, records: true })
    
    const initialState = {
        empName: [],
        startDt: moment().startOf('month').format('YYYY-MM-DD'),
        endDt: moment().format('YYYY-MM-DD')
    }
    const [state, setState] = useState(initialState)
    const [initialFetchDone, setInitialFetchDone] = useState(false);

    // Confirm Modal State
    const [isAcceptAllModalOpen, setIsAcceptAllModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        actionType: null,
        rowData: null,
        loading: false
    });

    const closeConfirmModal = () => {
        if (!confirmModal.loading) {
            setConfirmModal(prev => ({ ...prev, isOpen: false, rowData: null }));
        }
    };

    const handleConfirm = async () => {
        const { actionType, rowData } = confirmModal;
        setConfirmModal(prev => ({ ...prev, loading: true }));
        
        try {
            if (actionType === 'accept') {
                await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/action/acceptWfh?id=${rowData.REQUEST_ID}`, { params: { name: user.name } })
                notify.success("WFH request accepted")
            } else if (actionType === 'reject') {
                await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/action/rejectWfh?id=${rowData.REQUEST_ID}`)
                notify.success("WFH request rejected")
            } else if (actionType === 'delete') {
                await ApiClient.delete(`${process.env.REACT_APP_BASE_URL}/leave/deleteWfh`, { data: { data: rowData } })
                notify.success("WFH request deleted")
            } else if (actionType === 'rejectDelete') {
                await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/leave/rejectDeleteWfh`, { REQUEST_ID: rowData.REQUEST_ID, STATUS: "Reject" })
                notify.success("Delete Request Rejected Successfully");
            } else if (actionType === 'acceptDelete') {
                await ApiClient.delete(`${process.env.REACT_APP_BASE_URL}/leave/deleteWfh`, { data: { data: rowData } })
                notify.success("WFH Deleted Successfully");
            }
            await fetchNewRequests();
            await getRecordsByDate();
        } catch (error) {
            console.log(error)
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
            notify.error(errmsg)
        } finally {
            closeConfirmModal();
        }
    };
    const acceptAllWfhReqs = async (selectedRows) => {
        if (!selectedRows || selectedRows.length === 0) return;
        try {
            for (const row of selectedRows) {
                await ApiClient.get(
                    `${process.env.REACT_APP_BASE_URL}/action/acceptWfh/?id=${row.REQUEST_ID}`,
                    { params: { name: user?.name } }
                );
            }
            await fetchNewRequests();
            await getRecordsByDate();
            notify.success("Selected pending WFH requests accepted successfully");
        } catch (error) {
            console.log(error);
            notify.error("Failed to accept some WFH requests");
        }
    };

    const handleAcceptAllSelected = async (selectedRows) => {
        setActionLoading({ type: 'acceptAll', id: null });
        try {
            await acceptAllWfhReqs(selectedRows);
        } finally {
            setIsAcceptAllModalOpen(false);
            setActionLoading(null);
        }
    };

    const confirmAction = (type, rowData) => {
        let title = '';
        let message = '';
        
        if (type === 'accept') {
            title = 'Accept WFH Request';
            message = `Are you sure you want to accept the WFH request for ${rowData.EMP_NAME}?`;
        } else if (type === 'reject') {
            title = 'Reject WFH Request';
            message = `Are you sure you want to reject the WFH request for ${rowData.EMP_NAME}?`;
        } else if (type === 'delete') {
            title = 'Delete WFH Request';
            message = `Are you sure you want to delete the WFH request for ${rowData.EMP_NAME}? This action cannot be undone.`;
        } else if (type === 'rejectDelete') {
            title = 'Reject Deletion Request';
            message = `Are you sure you want to reject the deletion request for ${rowData.EMP_NAME}?`;
        } else if (type === 'acceptDelete') {
            title = 'Accept Deletion Request';
            message = `Are you sure you want to accept the deletion request for ${rowData.EMP_NAME}? This will permanently delete the WFH record.`;
        }
        
        setConfirmModal({
            isOpen: true,
            title,
            message,
            actionType: type,
            rowData,
            loading: false
        });
    };

    const pendingWfhCols = [{
          headerName: "Employee",
          field: "EMP_NAME",
          sortable: true,
          width: 200,
          renderCell: (params) => {
              return (
              <div className="flex items-center gap-3 py-1">
                  <UserAvatar name={params.row.EMP_NAME} size={38} />
                  <div className="flex flex-col justify-center">
                      <h3 className="text-[14px] font-semibold m-0 leading-tight text-slate-800">{params.row.EMP_NAME}</h3>
                      <div className="flex items-center gap-2 mt-1">
                          <p className="text-[11px] font-medium text-slate-500 m-0 uppercase tracking-widest">{params.row.EMP_ID || 'N/A'}</p>
                      </div>
                  </div>
              </div>
          )},
        }, 
        {
          headerName: "Request",
          field: "LEAVE_REQUEST",
          sortable: true,
          width: 140,
          renderCell: (params) => {
            const isDeleteReq = !!params?.row?.REQUEST_TYPE;
            const text = isDeleteReq ? "Awaiting Delete" : "Pending Approval";
            const bgClass = isDeleteReq ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600";
            const dotClass = isDeleteReq ? "bg-orange-500" : "bg-blue-500";
            return (
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold w-max ${bgClass}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></div>
                {text}
              </div>
            );
          },
        },
        {
                headerName: "#Days",
                field: "NO_OF_DAYS",
                sortable: true,
                width: 80,
                renderCell: (params) => (
                    <span className={"font-medium text-slate-700 text-[13px]"} >{Number(params?.row?.NO_OF_DAYS)}</span>
                )
            }, {
                headerName: "Start Date",
                field: "START_DATE",
                sortable: true,
                width: 120,
                renderCell: (params) => (
                    <span className={"text-slate-600 text-[13px]"} >{moment(params.row?.START_DATE).format("DD-MMM-YY")}</span>
                )
            }, {
                headerName: "End Date",
                field: "END_DATE",
                sortable: true,
                width: 120,
                renderCell: (params) => (
                    <span className={"text-slate-600 text-[13px]"} >{moment(params.row?.END_DATE).format("DD-MMM-YY")}</span>
                )
            }, {
                headerName: "Reason",
                field: "REASON",
                sortable: true,
                flex: 1,
                minWidth: 150,
                renderCell: (params) => (
                    <span className={"text-slate-600 text-[13px]"} >{params?.row?.REASON}</span>
                )
            },

            {
                headerName: "Action",
                field: "ACTION",
                sortable: false,
                width: 120,
                renderCell: (params) => (
                    <div className="flex items-center gap-3">
                    {!params?.row?.REQUEST_TYPE ?
                        <>
                            <Tooltip title="Accept">
                                <div className="w-7 h-7 flex items-center justify-center transition-colors rounded-md cursor-pointer hover:bg-green-50" onClick={() => confirmAction('accept', params.row)}>
                                    <CheckCircle2 size={20} className="text-green-600" />
                                </div>
                            </Tooltip>
                            <Tooltip title="Reject">
                                <div className="w-7 h-7 flex items-center justify-center transition-colors rounded-md cursor-pointer hover:bg-red-50" onClick={() => confirmAction('reject', params.row)}>
                                    <XCircle size={20} className="text-red-600" />
                                </div>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <div className="w-7 h-7 flex items-center justify-center transition-colors rounded-md cursor-pointer hover:bg-slate-100" onClick={() => confirmAction('delete', params.row)}>
                                    <Trash2 size={16} className="text-slate-600" />
                                </div>
                            </Tooltip>
                        </> :
                        <>
                            <Tooltip title="Reject Deletion">
                                <div className="w-7 h-7 flex items-center justify-center transition-colors rounded-md cursor-pointer hover:bg-red-50" onClick={() => confirmAction('rejectDelete', params.row)}>
                                    <XCircle size={20} className="text-red-600" />
                                </div>
                            </Tooltip>
                            <Tooltip title="Accept Deletion">
                                <div className="w-7 h-7 flex items-center justify-center transition-colors rounded-md cursor-pointer hover:bg-slate-100" onClick={() => confirmAction('acceptDelete', params.row)}>
                                    <Trash2 size={16} className="text-slate-600" />
                                </div>
                            </Tooltip>
                        </>
                    }
                    </div>
                )
            }
            ];
            

    const fetchNewRequests = async () => {
        try {
            setLoading(prev => ({...prev, pending: true}));
            
            const { data } = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/admin/newWFHRequest`, { params: { mail: user?.mail, date: moment(new Date()-ms('15d')).format('YYYY-MM-DD') } })
            let tempData=data?.filter((ele)=>ele?.STATUS==="Pending" || ele?.DELETE_STATUS ==="Request")
            setRows(tempData)
        }
        catch (error) {
            console.log(error)
        } finally {
            setLoading(prev => ({...prev, pending: false}));
        }
    }

    const recordsWfhCols = [{
                headerName: "Emp ID",
                field: "EMP_ID",
                sortable: true,
                width: 100,
                renderCell: (params) => <span className="text-slate-600 font-medium text-[13px]">{params.value}</span>
            }, {
                headerName: "Name",
                field: "EMP_NAME",
                sortable: true,
                width: 220,
                renderCell: (params) => <span className="text-slate-700 font-medium text-[13px]">{params.value}</span>
            }, {
                headerName: "#Days",
                field: "NO_OF_DAYS",
                sortable: true,
                width: 75,
                renderCell: (params) => <span className="text-slate-700 font-medium text-[13px]">{Number(params.value)}</span>
            }, {
                headerName: "Start Date",
                field: "START_DATE",
                sortable: true,
                width: 130,
                valueGetter: (params) => {
                    return params.row?.START_DATE ? moment(params.row?.START_DATE).format("DD-MMM-YYYY") : "";
                },
                renderCell: (params) => <span className="text-slate-600 text-[13px]">{params.value}</span>
            }, {
                headerName: "End Date",
                field: "END_DATE",
                sortable: true,
                width: 130,
                valueGetter: (params) => {
                    return params.row?.END_DATE ? moment(params.row?.END_DATE).format("DD-MMM-YYYY") : "";
                },
                renderCell: (params) => <span className="text-slate-600 text-[13px]">{params.value}</span>
            }, {
                headerName: "Reason",
                field: "REASON",
                sortable: true,
                width: 200,
                renderCell: (params) => <span className="text-slate-600 text-[13px]">{params.value}</span>
            }, {
                headerName: "Status",
                field: "STATUS",
                sortable: true,
                width: 130,
                valueGetter: (params) => {
                    return (params.row?.STATUS) ? (params.row?.STATUS) : "Pending";
                },
                renderCell: (params) => {
                    const s = params.value || "Pending";
                    let bg = 'bg-yellow-50 text-yellow-600';
                    let dot = 'bg-yellow-500';
                    if (s === 'Accepted' || s === 'Approved') {
                        bg = 'bg-green-50 text-green-600';
                        dot = 'bg-green-500';
                    } else if (s === 'Rejected' || s === 'Declined') {
                        bg = 'bg-red-50 text-red-600';
                        dot = 'bg-red-500';
                    }
                    return (
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${bg} w-max`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${dot}`}></div>
                            {s}
                        </div>
                    );
                }
            }
            ];
            

    const getRecordsByDate = async () => {
        try {
            setLoading(prev => ({...prev, records: true}));
            
            
            if (state.startDt && state.endDt) {
                if (new Date(state.startDt) <= new Date(state.endDt)) {
                    const payload = {
                        startDt: state.startDt,
                        endDt: state.endDt,
                    };
                    if (state.empName && state.empName.length > 0) {
                        payload.code = state.empName.map(e => e.EMP_CODE);
                    }
                    
                    const { data } = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/getWfhRecords`, payload)
                    if (data.length === 0) {
                        // notify.error("No matching records found")
                        setWfhRows([])
                    } else {
                        setWfhRows(data)
                    }
                } else notify.error("Start Date should be less than End Date")
            }
            else {
                notify.error("Start Date and End Date fields are mandatory")
            }
        }
        catch (error) {
            console.log(error)
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
            notify.error(errmsg)
        } finally {
            setLoading(prev => ({...prev, records: false, isInitial: false}))
        }
    }

    function handleChange(evt) {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value
        });
    }

    const initDateRange = () => {
        const minsec = ms('0d')
        const min_date = new Date(new Date(state.startDt) - minsec);
        const max_date = new Date(new Date());
        setMinDate(moment(min_date).format('YYYY-MM-DD'));
        setMaxDate(moment(max_date).format('YYYY-MM-DD'));
    }

    useEffect(() => {
        initDateRange();
    }, [state.startDt]);

    useEffect(() => {
        fetchNewRequests();
    }, [user])

    useEffect(() => {
        if (!initialFetchDone && state.startDt && state.endDt) {
            getRecordsByDate();
            setInitialFetchDone(true);
        }
    }, [state.startDt, state.endDt, initialFetchDone]);

    return (
        <div className="flex flex-col h-full relative overflow-y-auto w-full space-y-3">
            <AcceptAllWfhModal 
                isOpen={isAcceptAllModalOpen}
                onClose={() => setIsAcceptAllModalOpen(false)}
                onConfirm={handleAcceptAllSelected}
                wfhs={rows}
                loading={actionLoading?.type === 'acceptAll'}
            />
            
            {/* Action Confirm Modal */}
            <ActionConfirmModal 
                isOpen={confirmModal.isOpen}
                onClose={closeConfirmModal}
                onConfirm={handleConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                loading={confirmModal.loading}
                type={confirmModal.actionType === 'delete' || confirmModal.actionType === 'acceptDelete' || confirmModal.actionType === 'reject' || confirmModal.actionType === 'rejectDelete' ? 'danger' : 'info'}
                confirmText={confirmModal.actionType === 'delete' || confirmModal.actionType === 'acceptDelete' ? 'Delete' : confirmModal.actionType === 'reject' || confirmModal.actionType === 'rejectDelete' ? 'Reject' : 'Accept'}
            />

            {/* Top Row: Pending WFH Requests */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-shrink-0">
                <CentralizedTable
                    isLoading={loading.pending}
                    getRowId={row => row.EMP_NAME + row.START_DATE + row.END_DATE + row.STATUS}
                    rows={rows}
                    columns={pendingWfhCols}
                    autoHeight
                    renderTopToolbarCustomActions={() => (
                        <div className="flex items-center gap-3 py-2 px-2">
                            {/* Icon */}
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-orange-100 bg-orange-50">
                                <ClipboardList
                                    size={17}
                                    strokeWidth={2.2}
                                    className="text-orange-600"
                                />
                            </div>

                            {/* Content */}
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 m-0">
                                        Pending WFH Requests
                                    </h2>

                                    {rows.length > 0 && (
                                        <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-orange-100 px-2 text-xs font-semibold text-orange-700">
                                            {rows.length}
                                        </span>
                                    )}
                                </div>

                                <p className="text-xs text-slate-500 m-0 hidden sm:block">
                                    Review and process pending employee WFH requests awaiting approval.
                                </p>
                            </div>
                        </div>
                    )}
                    customInternalActions={() => (
                        rows.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setIsAcceptAllModalOpen(true)}
                                disabled={actionLoading !== null}
                                className={`whitespace-nowrap px-2.5 py-1.5 border border-blue-200 text-blue-600 bg-white shadow-sm text-sm font-medium rounded-[6px] transition-colors flex items-center gap-2 ml-2 ${actionLoading !== null ? 'cursor-not-allowed opacity-50' : 'hover:bg-blue-50'}`}
                            >
                                {actionLoading?.type === 'acceptAll' ? <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
                                Accept All
                            </button>
                        )
                    )}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 5, page: 0 },
                        },
                    }}
                       muiTableContainerProps={{
                            sx: {
                                minHeight: "auto",
                                height: 'auto',
                                flex: 1,
                                overflow: 'auto',
                            },
                        }}
                    pageSizeOptions={[5, 10, 25]}
                    hideFooterSelectedRowCount
                />
            </div>

            {/* Bottom Row: WFH Records Filter and Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0 mt-3">
                <div className="p-4 border-b border-slate-200 bg-slate-50/50">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="w-full">
                            <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Employee Name</label>
                            <Autocomplete
                                multiple
                                limitTags={1}
                                disableCloseOnSelect
                                renderTags={() => null}
                                options={employees ? employees.filter(el => el.STATUS === "Active") : []}
                                getOptionLabel={(option) => option ? `${option.EMP_NAME} (${option.EMP_ID || option.EMP_CODE})` : ""}
                                isOptionEqualToValue={(option, value) => option.EMP_CODE === value.EMP_CODE}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...restProps } = props;
                                    return (
                                        <li key={option.EMP_CODE || key} {...restProps}>
                                            <div className="flex items-center w-full">
                                                <span className={`flex-1 ${selected ? 'font-semibold' : ''}`}>{option.EMP_NAME} ({option.EMP_ID || option.EMP_CODE})</span>
                                            </div>
                                        </li>
                                    );
                                }}
                                value={state.empName}
                                onChange={(e, val) => handleChange({ target: { name: "empName", value: val } })}
                                size="small"
                                className="w-full bg-white text-sm"
                                ListboxProps={{ sx: { fontSize: '14px' } }}
                                renderInput={(params) => <TextField {...params} placeholder={state.empName.length > 0 ? `${state.empName.length} Selected` : "All Employees"} />}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        minHeight: '40px',
                                        paddingTop: '0 !important',
                                        paddingBottom: '0 !important',
                                        fontSize: '14px'
                                    },
                                    '& .MuiAutocomplete-input': {
                                        paddingTop: '0 !important',
                                        paddingBottom: '0 !important',
                                    }
                                }}
                            />
                        </div>
                        <div className="w-full">
                            <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Start Date <span className="text-red-500">*</span></label>
                            <TextField 
                                type="date" 
                                size="small" 
                                fullWidth
                                name="startDt"
                                value={state.startDt} 
                                onChange={handleChange} 
                                InputLabelProps={{ shrink: true }} 
                                className="bg-white"
                                sx={{ '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px', fontSize: '14px' } }}
                            />
                        </div>
                        <div className="w-full">
                            <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">End Date <span className="text-red-500">*</span></label>
                            <TextField 
                                type="date" 
                                size="small" 
                                fullWidth
                                name="endDt"
                                value={state.endDt} 
                                onChange={handleChange} 
                                InputLabelProps={{ shrink: true }} 
                                className="bg-white"
                                sx={{ '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px', fontSize: '14px' } }}
                            />
                        </div>
                        <div className="w-full">
                            <button
                                onClick={getRecordsByDate}
                                disabled={loading.records}
                                className="w-full h-[40px] px-6 bg-brand-600 text-white text-sm font-semibold rounded-[6px] hover:bg-brand-700 focus:ring-4 focus:ring-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex justify-center items-center gap-2"
                            >
                                {loading.records ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                                Search
                            </button>
                        </div>
                    </div>
                    {state.empName.length > 0 && (
                        <>
                            <hr className="my-5 border-slate-200" />
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                <div className="flex flex-wrap gap-2 flex-1 items-center">
                                    <span className="text-xs font-bold text-slate-500 uppercase mr-1">Selected Employees:</span>
                                    {state.empName.map((emp) => (
                                        <Chip 
                                            key={emp.EMP_CODE} 
                                            label={emp.EMP_NAME} 
                                            size="small" 
                                            className='font-semibold'
                                            onDelete={() => {
                                                handleChange({ target: { name: "empName", value: state.empName.filter(e => e.EMP_CODE !== emp.EMP_CODE) } })
                                            }}
                                            deleteIcon={<X size={14} strokeWidth={2.5} />}
                                            sx={{ 
                                                backgroundColor: '#eff6ff', 
                                                color: '#2563eb', 
                                                '& .MuiChip-deleteIcon': {
                                                    color: '#3b82f6', 
                                                    '&:hover': { color: '#1d4ed8' } 
                                                }
                                            }}
                                        />
                                    ))}
                                    <button 
                                        onClick={() => handleChange({ target: { name: "empName", value: [] } })}
                                        className="text-[12px] text-red-500 hover:text-red-700 font-medium ml-2 underline underline-offset-2"
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex-1 overflow-auto relative p-0 m-0 h-full flex flex-col">
                    <CentralizedTable
                        isLoading={loading.isInitial || loading.records}
                        renderTopToolbarCustomActions={() => (
                            <div className="flex items-center gap-3 py-2 px-2">
                                {/* Icon */}
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-blue-50">
                                    <History
                                        size={17}
                                        strokeWidth={2.2}
                                        className="text-blue-600"
                                    />
                                </div>
    
                                {/* Content */}
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 m-0">
                                            WFH Records
                                        </h2>
                                    </div>
    
                                    <p className="text-xs text-slate-500 m-0 hidden sm:block">
                                        View historical work from home records and statuses.
                                    </p>
                                </div>
                            </div>
                        )}
                        getRowId={row => row.REQUEST_ID}
                        rows={wfhRows}
                        columns={recordsWfhCols}
                        autoHeight
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        hideFooterSelectedRowCount
                        muiTableContainerProps={{
                            sx: {
                                minHeight: "auto",
                                height: 'auto',
                                flex: 1,
                                overflow: 'auto',
                            },
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export default WFHTabSA